<script lang="ts">
/**
 * List row for a container, used across dashboard, index, and search.
 *
 * Displays thumbnail, name, location path (or child counts), and
 * progress indicator. Four variants control what appears in each
 * context:
 *
 * - `root`      — location path subtitle, progress bar + chevron
 * - `drilled`   — child counts subtitle, progress bar + chevron
 * - `search`    — location path subtitle, progress bar + chevron
 * - `dashboard` — location path subtitle, compact badge, no chevron
 *
 * @example
 * ```vue
 * <ContainerRow :container="container" variant="dashboard" />
 * <ContainerRow :container="container" variant="root" />
 * ```
 */
export type ContainerRowVariant = "root" | "drilled" | "search" | "dashboard" | "picker"

export interface ContainerRowProps {
  /** Container data from the backend. */
  container: ContainerSummary
  /** Display variant. @defaultValue `"root"` */
  variant?: ContainerRowVariant
  /** Override the default link destination. */
  to?: string
}
</script>

<script setup lang="ts">
import { SbListItem, SbListSection, SbListLabel, SbProgressBar } from "@unio/saltbox-ui"
import { computed } from "vue"

import { ICON } from "@/lib/icons"
import { containerPath, containersPath } from "@/routes"
import type { ContainerSummary } from "@/types/models"

const props = withDefaults(defineProps<ContainerRowProps>(), {
  variant: "root",
})

const linkTo = computed(() => {
  if (props.to) return props.to
  if (props.variant === "picker") return undefined
  if (props.container.archived_at) return containerPath(props.container.id)
  if (props.variant === "dashboard") {
    return containerPath(props.container.id)
  }
  return `${containersPath()}?parent_id=${props.container.id}`
})

const subtitle = computed(() => {
  if (props.variant === "drilled") {
    const parts: string[] = []
    if (props.container.sub_container_count) {
      parts.push(`${props.container.sub_container_count} sub`)
    }
    if (props.container.item_count != null) {
      parts.push(`${props.container.item_count} items`)
    }
    return parts.length ? parts.join(" · ") : props.container.location_path
  }
  return props.container.location_path
})

const showChevron = computed(() => props.variant !== "dashboard" && props.variant !== "picker")

const hasProgress = computed(() => {
  if (props.variant === "picker") return false
  return props.container.inventoried_count != null && props.container.total_item_count != null
})
</script>

<template>
  <SbListItem clickable :to="linkTo">
    <SbListSection avatar>
      <UAvatar
        :src="container.thumb_url ?? undefined"
        :alt="container.name"
        size="lg"
        :ui="{ image: 'object-cover' }"
      />
    </SbListSection>

    <SbListSection>
      <div class="flex items-center gap-2">
        <SbListLabel :lines="1">{{ container.name }}</SbListLabel>
        <UBadge v-if="container.archived_at" color="warning" variant="subtle" size="sm"
          >Archived</UBadge
        >
      </div>
      <SbListLabel caption :lines="1">{{ subtitle }}</SbListLabel>
    </SbListSection>

    <!-- Progress: dashboard shows compact badge, others show stacked bar -->
    <SbListSection v-if="hasProgress" side>
      <UBadge v-if="variant === 'dashboard'" color="neutral" variant="subtle" size="sm">
        <span class="tabular-nums"
          >{{ container.inventoried_count }}/{{ container.total_item_count }}</span
        >
      </UBadge>
      <div v-else class="flex flex-col items-end gap-1">
        <SbProgressBar
          :current="container.inventoried_count!"
          :total="container.total_item_count!"
          size="sm"
        />
      </div>
    </SbListSection>

    <SbListSection v-if="showChevron" side class="shrink-0">
      <UIcon :name="ICON.chevronRight" class="text-muted size-4" />
    </SbListSection>
  </SbListItem>
</template>
